/**
 * Timer hook for countdown functionality.
 *
 * Provides reusable timer countdown logic for exam modules.
 * Supports both always-on and conditional timer modes.
 *
 * @module useTimer
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Options for timer hook
 */
interface UseTimerOptions {
  /** Initial time in seconds */
  initialTime: number;
  /** Whether timer is enabled (default: true) */
  enabled?: boolean;
  /** Callback when timer reaches zero */
  onExpire?: () => void;
  /** Whether to auto-start timer (default: true) */
  autoStart?: boolean;
  /**
   * Optional wall-clock epoch-ms capturing when the timer first started.
   * When provided, `initialTime` is interpreted as the *original* budget and
   * the hook seeds `timeRemaining` with `max(0, initialTime - (now - startedAt)/1000)`.
   * Used by draft recovery so that a refresh/reopen does not reset the timer.
   */
  startedAt?: number | null;
}

/**
 * Return type for timer hook
 */
interface UseTimerReturn {
  /** Current time remaining in seconds */
  timeRemaining: number;
  /** Reset timer to a new time value */
  reset: (newTime: number) => void;
  /** Manually tick the timer (decrement by 1) */
  tick: () => void;
}

/**
 * Hook for managing countdown timer.
 *
 * Automatically decrements time every second when enabled.
 * Supports conditional enabling/disabling and expiration callback.
 *
 * @param options - Timer configuration
 * @returns Object containing timeRemaining, reset, and tick functions
 *
 * @example
 * ```typescript
 * // Always-on timer
 * const { timeRemaining, reset } = useTimer({
 *   initialTime: 2700, // 45 minutes
 * });
 *
 * // Conditional timer
 * const { timeRemaining } = useTimer({
 *   initialTime: 120,
 *   enabled: isReviewPeriod,
 *   onExpire: () => handleTimeExpired(),
 * });
 * ```
 */
function computeInitialTime(initialTime: number, startedAt: number | null | undefined): number {
  if (startedAt === null || startedAt === undefined) {
    return initialTime;
  }
  const elapsedSeconds = Math.max(0, (Date.now() - startedAt) / 1000);
  return Math.max(0, Math.floor(initialTime - elapsedSeconds));
}

export function useTimer({
  initialTime,
  enabled = true,
  onExpire,
  autoStart = true,
  startedAt = null,
}: UseTimerOptions): UseTimerReturn {
  const [timeRemaining, setTimeRemaining] = useState(() =>
    computeInitialTime(initialTime, startedAt),
  );
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    setTimeRemaining(computeInitialTime(initialTime, startedAt));
  }, [initialTime, startedAt]);

  // Timer countdown
  useEffect(() => {
    if (!enabled || !autoStart || timeRemaining <= 0) {
      return;
    }

    const handleExpire = () => {
      if (onExpireRef.current) {
        onExpireRef.current();
      }
    };

    const handleTick = () => {
      setTimeRemaining((prev) => {
        const newTime = Math.max(0, prev - 1);
        
        // Call onExpire when timer reaches zero
        if (newTime === 0) {
          // Use setTimeout to avoid calling during render
          setTimeout(handleExpire, 0);
        }
        
        return newTime;
      });
    };

    const timer = setInterval(handleTick, 1000);

    return () => clearInterval(timer);
  }, [enabled, autoStart, timeRemaining]);

  const reset = useCallback((newTime: number) => {
    setTimeRemaining(newTime);
  }, []);

  const tick = useCallback(() => {
    setTimeRemaining((prev) => Math.max(0, prev - 1));
  }, []);

  return {
    timeRemaining,
    reset,
    tick,
  };
}

